import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import Header from './Header';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Administrator = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [stats, setStats] = useState({ 
    total_users: 0, 
    total_transactions: 0, 
    total_amount: 0,
    breakdown: { 
      airtime: { count: 0, amount: 0 }, 
      data: { count: 0, amount: 0, gigs: 0 }, 
      other: { count: 0 } 
    } 
  });
  const [balances, setBalances] = useState({ easyaccess: 0, clubkonnect: 0, premiersub:0, gladtidings:0});
  const [transactions, setTransactions] = useState([]);
  
  const [filterType, setFilterType] = useState('All');
  const [timeframe, setTimeframe] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchAdminData = useCallback(async () => {
    if (!user) {
        setLoading(false);
        return;
    }
    
    try {
      setLoading(true);
      const idToken = await user.getIdToken();
      const headers = { 'Authorization': `Bearer ${idToken}` };

      let queryParams = `?filter_type=${timeframe}`;
      if (timeframe === 'custom') {
          if (customStartDate) queryParams += `&start_date=${new Date(customStartDate).toISOString()}`;
          if (customEndDate) queryParams += `&end_date=${new Date(customEndDate).toISOString()}`;
      }

      // Fetch stats
      const statsRes = await fetch(`${apiUrl}/api/v1/admin/stats${queryParams}`, { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Fetch balances
      const balancesRes = await fetch(`${apiUrl}/api/v1/admin/provider-balances`, { headers });
      if (balancesRes.ok) {
        const balancesData = await balancesRes.json();
        setBalances(balancesData);
      }

      // Fetch transactions
      const txRes = await fetch(`${apiUrl}/api/v1/admin/transactions${queryParams}`, { headers });
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData);
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  }, [user, timeframe, customStartDate, customEndDate]);

  useEffect(() => {
      if (timeframe !== 'custom') {
          fetchAdminData();
      }
  }, [timeframe, fetchAdminData]);

  const handleUserSearch = async (e) => {
    e.preventDefault();
    if (!userSearchTerm || !user) return;
    setSearchingUsers(true);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/users/${encodeURIComponent(userSearchTerm)}`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSearchedUsers(data);
      }
    } catch (e) { console.error(e); }
    setSearchingUsers(false);
  };

  const handleCustomDateSubmit = (e) => {
      e.preventDefault();
      fetchAdminData();
  }

  const getBalanceColor = (balance) => {
    if (balance < 1000) return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
    if (balance < 5000) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
  };

  const getStatusColor = (status) => {
      if (!status) return 'text-gray-500 bg-gray-100';
      const s = String(status).toLowerCase();
      if (s.includes('success')) return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      if (s.includes('fail') || s.includes('error')) return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      if (s.includes('pend')) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      return 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-300';
  }

  const filteredTransactions = transactions.filter(tx => {
    // Service type filter
    if (filterType !== 'All') {
        const type = (tx.type || '').toLowerCase();
        if (filterType === 'Airtime' && !type.includes('airtime')) return false;
        if (filterType === 'Data' && !type.includes('data')) return false;
        if (filterType === 'Other' && (type.includes('airtime') || type.includes('data'))) return false;
    }
    
    // Search term filter (date or reference)
    if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const dateStr = new Date(tx.timestamp).toLocaleString().toLowerCase();
        const refStr = tx.reference ? String(tx.reference).toLowerCase() : '';
        const idStr = tx.id ? String(tx.id).toLowerCase() : '';
        
        if (!dateStr.includes(term) && !refStr.includes(term) && !idStr.includes(term)) {
            return false;
        }
    }
    
    return true;
  });

  const safeFilteredTransactions = Array.isArray(filteredTransactions) ? filteredTransactions : [];

  const totalPages = Math.max(1, Math.ceil(safeFilteredTransactions.length / itemsPerPage));
  const currentTransactions = safeFilteredTransactions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [filterType, searchTerm, timeframe]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
      <div className={`pt-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <main className="max-w-7xl mx-auto py-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
              Admin Dashboard 
              {loading && <span className="ml-4 text-sm font-normal text-gray-500 animate-pulse">Refreshing...</span>}
            </h2>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">Timeframe:</span>
              <select 
                value={timeframe} 
                onChange={(e) => setTimeframe(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-sm"
              >
                <option value="all">All Time</option>
                <option value="day">Today</option>
                <option value="week">Past Week</option>
                <option value="month">Past Month</option>
                <option value="year">Past Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>
          
          {timeframe === 'custom' && (
            <form onSubmit={handleCustomDateSubmit} className="mb-6 flex flex-wrap items-end gap-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                <input 
                  type="date" 
                  value={customStartDate} 
                  onChange={e => setCustomStartDate(e.target.value)} 
                  required
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                <input 
                  type="date" 
                  value={customEndDate} 
                  onChange={e => setCustomEndDate(e.target.value)} 
                  required
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <button type="submit" className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors">
                Apply Filter
              </button>
            </form>
          )}

          {!user ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                <p>Please log in to view this page.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Provider Balances */}
              <div>
                {/* <h3 className="text-xl font-semibold mb-4">Provider Wallet Balances</h3> */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`rounded-lg shadow p-6 border flex justify-between items-center ${getBalanceColor(balances?.easyaccess || 0)}`}>
                    <div>
                      <h4 className="text-lg font-bold">Easy</h4>
                      <p className="text-sm opacity-80 mt-1">Status: {(balances?.easyaccess || 0) < 1000 ? 'Critical' : (balances?.easyaccess || 0) < 5000 ? 'Warning' : 'Healthy'}</p>
                    </div>
                    <div className="text-3xl font-extrabold">₦{Number(balances?.easyaccess || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  </div>
                  <div className={`rounded-lg shadow p-6 border flex justify-between items-center ${getBalanceColor(balances?.clubkonnect || 0)}`}>
                    <div>
                      <h4 className="text-lg font-bold">Connect</h4>
                      <p className="text-sm opacity-80 mt-1">Status: {(balances?.clubkonnect || 0) < 1000 ? 'Critical' : (balances?.clubkonnect || 0) < 5000 ? 'Warning' : 'Healthy'}</p>
                    </div>
                    <div className="text-3xl font-extrabold">₦{Number(balances?.clubkonnect || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  </div>
                   <div className={`rounded-lg shadow p-6 border flex justify-between items-center ${getBalanceColor(balances?.premiersub || 0)}`}>
                    <div>
                      <h4 className="text-lg font-bold">Premier</h4>
                      <p className="text-sm opacity-80 mt-1">Status: {(balances?.premiersub || 0) < 1000 ? 'Critical' : (balances?.premiersub || 0) < 5000 ? 'Warning' : 'Healthy'}</p>
                    </div>
                    <div className="text-3xl font-extrabold">₦{Number(balances?.premiersub || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  </div>
                   <div className={`rounded-lg shadow p-6 border flex justify-between items-center ${getBalanceColor(balances?.gladtidings || 0)}`}>
                    <div>
                      <h4 className="text-lg font-bold">Glad</h4>
                      <p className="text-sm opacity-80 mt-1">Status: {(balances?.gladtidings || 0) < 1000 ? 'Critical' : (balances?.gladtidings || 0) < 5000 ? 'Warning' : 'Healthy'}</p>
                    </div>
                    <div className="text-3xl font-extrabold">₦{Number(balances?.gladtidings || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  </div>
                </div>
              </div>

              {/* Top Stats Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-blue-500">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</h3>
                  <p className="text-3xl font-bold mt-2">{stats.total_users}</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-indigo-500">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Transactions</h3>
                  <div className="flex justify-between items-baseline mt-2">
                    <p className="text-3xl font-bold">{stats?.total_transactions || 0}</p>
                    <p className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">
                      ₦{(stats?.total_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-green-500">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Airtime Transactions</h3>
                    <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <p className="text-2xl font-bold">{stats?.breakdown?.airtime?.count || 0}</p>
                    <p className="text-md font-semibold text-green-600 dark:text-green-400">
                      ₦{(stats?.breakdown?.airtime?.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-purple-500">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Transactions</h3>
                    <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-full">
                      <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.906 14.142 0M1.394 9.393c5.857-5.857 15.355-5.858 21.213 0" /></svg>
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline mb-1">
                    <p className="text-2xl font-bold">{stats?.breakdown?.data?.count || 0}</p>
                    <p className="text-md font-semibold text-purple-600 dark:text-purple-400">
                      ₦{(stats?.breakdown?.data?.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  </div>
                  <p className="text-xs font-medium text-gray-500">
                    <span className="font-bold text-gray-700 dark:text-gray-300">{(stats?.breakdown?.data?.gigs || 0).toLocaleString(undefined, {maximumFractionDigits: 2})} GB</span> Sold
                  </p>
                </div>
              </div>

              {/* Analytics Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8 min-w-0 relative z-0">
                {/* Bar Graph Analysis */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 min-w-0 w-full overflow-hidden relative z-0">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">Volume & Value Comparison</h3>
                  <div className="h-80 w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { name: 'Airtime', count: stats?.breakdown?.airtime?.count || 0, amount: stats?.breakdown?.airtime?.amount || 0 },
                          { name: 'Data', count: stats?.breakdown?.data?.count || 0, amount: stats?.breakdown?.data?.amount || 0 }
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                        <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                        <Tooltip wrapperStyle={{ zIndex: 10 }} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="Transaction Count" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="right" dataKey="amount" fill="#82ca9d" name="Total Amount (₦)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Donut Pie Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 relative z-0 flex flex-col items-center min-w-0 w-full overflow-hidden">
                  <h3 className="text-xl font-semibold mb-4 w-full text-gray-800 dark:text-white">Sales Distribution (₦)</h3>
                  <div className="h-80 w-full flex justify-center items-center min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Airtime', value: stats?.breakdown?.airtime?.amount || 0 },
                            { name: 'Data', value: stats?.breakdown?.data?.amount || 0 }
                          ].filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius="60%"
                          outerRadius="80%"
                          fill="#8884d8"
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {
                            [
                              { name: 'Airtime', value: stats?.breakdown?.airtime?.amount || 0 },
                              { name: 'Data', value: stats?.breakdown?.data?.amount || 0 }
                            ].filter(d => d.value > 0).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.name === 'Airtime' ? '#22c55e' : '#a855f7'} />
                            ))
                          }
                        </Pie>
                        <Tooltip wrapperStyle={{ zIndex: 10 }} formatter={(value) => `₦${Number(value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                    {((stats?.breakdown?.airtime?.amount || 0) === 0 && (stats?.breakdown?.data?.amount || 0) === 0) && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none pt-12">
                        <span className="text-gray-400 font-medium">No Sales Data</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mt-8">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <h3 className="text-xl font-semibold whitespace-nowrap">Recent Transactions</h3>
                  
                  <div className="flex flex-col sm:flex-row w-full lg:w-auto gap-4">
                    <div className="relative w-full sm:w-64">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Search date or reference..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-3 py-1.5 w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 dark:bg-gray-700 text-sm"
                      />
                    </div>
                  
                    <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-md">
                      {['All', 'Airtime', 'Data', 'Other'].map(type => (
                        <button
                          key={type}
                          onClick={() => setFilterType(type)}
                          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${filterType === type ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ref ID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type / Network</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentTransactions.length > 0 ? (
                        currentTransactions.map((tx) => (
                          <tr key={tx.id || Math.random().toString()} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(tx.timestamp).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-400">
                              {tx.reference ? (tx.reference.length > 12 ? tx.reference.substring(0, 12) + '...' : tx.reference) : (tx.id ? tx.id.substring(0, 8) + '...' : 'N/A')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              <span className="font-semibold">{tx.type || 'N/A'}</span>
                              <span className="text-xs ml-2 text-gray-400">({typeof tx.network === 'object' ? tx.network?.name : tx.network || 'N/A'})</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                              ₦{isNaN(parseFloat(tx.amount)) ? '0' : parseFloat(tx.amount).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {tx.response && (tx.response.status || tx.response.success) ? (
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(tx.response.status || (tx.response.success === 'true' ? 'success' : 'fail'))}`}>
                                  {tx.response.status || (tx.response.success === 'true' ? 'Success' : 'Fail')}
                                </span>
                              ) : (
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${tx.type === "Deposit" ? getStatusColor("success") : getStatusColor("pending")}`}>
                                  {tx.type === "Deposit" ? "Success" : "Unknown"}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                            <div className="flex flex-col items-center">
                              <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <p className="text-lg font-medium">No transactions found</p>
                              <p className="text-sm mt-1">Try adjusting your timeframe or search filters.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination Controls */}
                {safeFilteredTransactions.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4 sm:mb-0">
                      <span>Show</span>
                      <select 
                        value={itemsPerPage} 
                        onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        className="mx-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 px-2 py-1 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span>entries</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handlePageChange(currentPage - 1)} 
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded text-sm font-medium ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'}`}
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button 
                        onClick={() => handlePageChange(currentPage + 1)} 
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded text-sm font-medium ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'}`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User Lookup */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">Quick User Lookup</h3>
                <form onSubmit={handleUserSearch} className="flex gap-4 mb-4">
                  <input type="text" value={userSearchTerm} onChange={(e) => setUserSearchTerm(e.target.value)} placeholder="Search by email or UID" className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white" required />
                  <button type="submit" disabled={searchingUsers} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow transition-colors">
                    {searchingUsers ? 'Searching...' : 'Search'}
                  </button>
                </form>
                {searchedUsers.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {searchedUsers.map(u => (
                          <tr key={u.uid}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-400">{u.uid}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{u.firstName} {u.lastName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{u.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{u.role || 'user'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Administrator;
